<template>
  <div
    class="media-container-layer"
    :class="[attrsClass, { 'edit-mode': mode === 'edit' }]"
    :style="containerStyle"
    ref="containerElement"
  >
    <slot :is-visible="isVisible" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick, computed, useAttrs, provide } from "vue";
import { useLazyLoad } from '../../../composables/useLazyLoad';
import { useContainerResize } from '../../../vendor/VUseMask';

interface ImageProps {
  width?: number;
  height?: number;
}

interface VideoProps {
  width?: number;
  height?: number;
}

interface MaskProps {
  enabled?: boolean;
  maskAspectRatio?: number;
}

interface Props {
  // Объект со всеми вычислениями из useMediaCard
  mediaCardState: ReturnType<(...args: any[]) => any>;
  // Базовые данные для расчета стилей
  maxHeight?: string;
  minHeight?: string;
  objectFit?: "cover" | "contain";
  mediaType?: "image" | "video";
  imageProps?: ImageProps;
  videoProps?: VideoProps;
  maskProps?: MaskProps;
  hasMask?: boolean;
  mode?: "view" | "edit";
}

const props = defineProps<Props>();

const attrs = useAttrs();

// Безопасно получаем class из attrs
const attrsClass = computed(() => {
  const classValue = attrs.class;
  if (!classValue) return [];
  if (typeof classValue === 'string') return [classValue];
  if (Array.isArray(classValue)) return classValue;
  return [];
});

const containerElement = ref<HTMLElement | null>(null);

// Используем useContainerResize для масштабирования координат
const { scale, containerSize } = useContainerResize(containerElement, {});

/**
 * Единый стиль для контейнера - используем готовые стили из composable
 */
const containerStyle = computed(() => {
  // Безпечна перевірка наявності mediaCardState та containerStyles
  if (!props.mediaCardState || !props.mediaCardState.containerStyles) {
    return {};
  }
  
  // aspectRatio-based layout: height is derived from width via CSS aspect-ratio,
  // so no JS mobile detection needed — it scales correctly at any viewport width.
  const styles: Record<string, string | number> = {
    ...props.mediaCardState.containerStyles.value,
  };

  // Добавляем borderRadius для edit-mode
  if (props.mediaCardState.borderRadius?.value !== undefined) {
    const borderRadiusValue = props.mediaCardState.borderRadius.value;
    if (borderRadiusValue !== null && borderRadiusValue !== undefined) {
      styles.borderRadius = `${borderRadiusValue}px`;
    }
  }
  
  return styles;
});

// Предоставляем масштаб для дочерних компонентов
provide('containerScale', scale);

// Ленивая загрузка медиа - используем containerElement напрямую
const { isVisible, raiseOnMounted, raiseOnUnmounted } = useLazyLoad(
  containerElement,
  {
    rootMargin: "400px", // Начинаем загрузку за 300px до появления элемента
  }
);

// Отслеживаем изменения containerElement и вызываем raiseOnMounted
watch(containerElement, (newVal) => {
  if (newVal) {
    nextTick(() => {
      raiseOnMounted();
    });
  }
}, { immediate: true });

onMounted(() => {
  // Убеждаемся, что ref установлен после монтирования
  nextTick(() => {
    if (containerElement.value) {
      raiseOnMounted();
    } else {
      // Если ref еще не установлен, пробуем через небольшую задержку
      setTimeout(() => {
        if (containerElement.value) {
          raiseOnMounted();
        }
      }, 100);
    }
  });
});

onUnmounted(() => {
  raiseOnUnmounted();
});

defineExpose({
  mediaViewerRef: containerElement,
  isVisible,
});
</script>

<style scoped>
.media-container-layer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* Always responsive — JS may set explicit px width but max-width caps it */
  max-width: 100%;
  /* height controlled by inline styles on desktop */
}

/* Narrow container: override explicit px height so aspect-ratio computes proportional
   height. Container query (not media query) — reacts to the actual rendered width of
   the nearest containment ancestor (each Block*CardView sets container-type:inline-size),
   so this works both on real narrow viewports and inside width-constrained previews
   (e.g. the office2 editor's mobile preview, which resizes a container, not the viewport). */
@container (max-width: 719px) {
  .media-container-layer {
    height: auto !important;
    min-height: unset !important;
    max-height: unset !important;
  }
}


.media-container-layer.edit-mode {
  border-radius: inherit;
}

.media-container-layer.edit-mode::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px dashed rgba(77, 76, 76, 0.5);
  border-radius: inherit;
  pointer-events: none;
  z-index: -1;
}
</style>

